import { LoadingIcon } from '@assets/icons';
import { Select } from '@components/form';
import { useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { GetWorkspacesNameListQuery, useGetWorkspacesNameListQuery } from '@generated/graphql';
import { Box, MenuItem } from '@mui/material';
import { OrganizationsSettingsWorkspaceUsageTable } from '@subsets/organizations';
import { useEffect, useState } from 'react';

type OrganizationsSettingsWorkspaceUsageProps = {
  organizationId?: string;
};

export const OrganizationsSettingsWorkspaceUsage = ({
  organizationId,
}: OrganizationsSettingsWorkspaceUsageProps) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const {
    data: workspaces,
    loading: workspacesLoading,
    fetchMore: fetchMoreWorkspaces,
  } = useGraphqlPagination<
    GetWorkspacesNameListQuery,
    GetWorkspacesNameListQuery['getWorkspaces'] extends readonly (infer ElementType)[]
      ? ElementType
      : never
  >(
    useGetWorkspacesNameListQuery({
      variables: {
        organizationId,
      },
    }),
    'getWorkspaces',
    'workspaceId',
  );

  useEffect(() => {
    if (workspaces?.length > 0) {
      setSelectedWorkspaceId(workspaces[0].workspaceId);
    }
  }, [workspaces]);

  const handleSelectChange = ({ target: { value } }) => {
    setSelectedWorkspaceId(value as string);
  };

  return (
    <>
      <Group alignItems="center" gap={4}>
        <Typography variant="subtitle1">Workspace usage</Typography>
        {workspacesLoading ? (
          <Group alignItems="center" height="34px">
            <LoadingIcon size={8} />
          </Group>
        ) : (
          <Box maxWidth="75%">
            {workspaces.length > 0 && selectedWorkspaceId && (
              <Select
                value={selectedWorkspaceId}
                onChange={handleSelectChange}
                fetchMore={fetchMoreWorkspaces}
              >
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.workspaceId} value={workspace.workspaceId}>
                    {workspace.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        )}
      </Group>
      <OrganizationsSettingsWorkspaceUsageTable selectedWorkspaceId={selectedWorkspaceId} />
    </>
  );
};
